import { FC, useMemo, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { alpha, Box, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { borderRadius } from '@config/themes/common/borderRadius';
import { useDirection } from '@hooks';
import { FileStatus } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';
import { getFileExtension, validateUploadFile } from '@libs/utils';
import { NoFlipTypography } from '@utils';

import { FileUploadItem } from './FileUploadItem';

export interface FileUploadProps {
  allowedFileTypes?: string[];
  maxFileSizeMb?: number;
  multiple?: boolean;
  maxFiles?: number;
  files?: FileUploadType[];
  onAdd?: (files: File[]) => void;
  onDelete?: (fileId: string) => void;
  onCancel?: (fileId: string) => void;
  onUndoDelete?: (fileId: string) => void;
  onRetryDelete?: (fileId: string) => void;
  onRetryUpload?: (fileId: string) => void;
  onRemoveFailedUpload?: (fileId: string) => void;
  disabled?: boolean;
  sizeVariant?: 'small' | 'large';
  className?: string;
  onValidationError?: (error: {
    type: 'size' | 'type' | 'maxFiles';
    fileName: string;
    fileExtension?: string;
    maxFiles?: number;
  }) => void;
  /** Optional render function for additional content below each file item */
  renderExtraContent?: (file: FileUploadType) => React.ReactNode;
  /** Optional render function for an inline badge in each file's meta row. */
  renderFileBadge?: (file: FileUploadType) => React.ReactNode;
  /** Optional predicate — when true for a file, that card shows a moving overlay. */
  isFileMoving?: (file: FileUploadType) => boolean;
  /** When true for a given file, its delete button is disabled. */
  isDeleteDisabled?: (file: FileUploadType) => boolean;
  /**
   * Extra "still working on it" signal, on top of the upload status this
   * component owns. A file can be fully uploaded and still be queued for — or
   * mid — analysis, which reads to the user as loading, not as done. The caller
   * supplies it because that work is theirs to track, not this component's.
   */
  isFileProcessing?: (file: FileUploadType) => boolean;
}

export const FileUpload: FC<FileUploadProps> = observer(
  ({
    allowedFileTypes = ['.pdf', '.doc', '.docx', '.xlsx', '.xls', '.png', '.jpeg', '.jpg'],
    maxFileSizeMb = 10,
    multiple = false,
    maxFiles,
    files = [],
    onAdd,
    onDelete,
    onCancel,
    onUndoDelete,
    onRetryDelete,
    onRetryUpload,
    onRemoveFailedUpload,
    disabled = false,
    sizeVariant = 'small',
    className,
    onValidationError,
    renderExtraContent,
    renderFileBadge,
    isFileMoving,
    isFileProcessing,
    isDeleteDisabled,
  }) => {
    const theme = useTheme();
    const { dir } = useDirection();
    const { t } = useTrans();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const validateFiles = (
      fileList: FileList | null,
    ): { validFiles: File[] } => {
      if (!fileList) return { validFiles: [] };

      const validFiles: File[] = [];

      // Count only active files (exclude deleted)
      const activeFilesCount = files.filter((file) => file.status !== FileStatus.DELETED).length;

      Array.from(fileList).forEach((file) => {
        const currentFileCount = activeFilesCount + validFiles.length;
        const validation = validateUploadFile(file, {
          allowedFileTypes,
          maxFileSizeMb,
          maxFiles,
          currentFileCount,
        });
        if (validation.valid) {
          validFiles.push(file);
        } else if (validation.error && onValidationError) {
          onValidationError({
            type: validation.error,
            fileName: file.name,
            fileExtension: getFileExtension(file.name),
            maxFiles: validation.error === 'maxFiles' ? maxFiles : undefined,
          });
        }
      });
      return { validFiles };
    };

    const handleFileSelect = (selectedFiles: FileList | null) => {
      if (!selectedFiles || disabled) return;

      const { validFiles } = validateFiles(selectedFiles);

      if (validFiles.length > 0 && onAdd) {
        onAdd(validFiles);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    /**
     * Checks if the mouse position is within the dropzone bounds
     */
    const isWithinDropzone = (event: React.DragEvent<HTMLDivElement>): boolean => {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      if (disabled) return;
      handleFileSelect(event.dataTransfer.files);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!disabled && isWithinDropzone(event)) {
        setIsDraggingOver(true);
      }
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!disabled && isWithinDropzone(event)) {
        setIsDraggingOver(true);
      }
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      // Only reset if we're actually leaving the dropzone (not just moving to a child element)
      if (!isWithinDropzone(event)) {
        setIsDraggingOver(false);
      }
    };

    const handleBrowseClick = () => {
      if (disabled) return;

      fileInputRef.current?.click();
    };

    const isLarge = sizeVariant === 'large';

    // Split by lifecycle rather than one mixed list. A file in flight and a
    // finished one answer different questions ("is it there yet?" vs "did it
    // land right?"), and interleaving them is most of why the list reads as
    // disorganised — a two-line uploading stub between full-height result cards
    // punches a hole in the grid. Upload order is preserved inside each group.
    const { inFlightFiles, settledFiles } = useMemo(() => {
      const isInFlight = (f: FileUploadType) =>
        f.status === FileStatus.QUEUED ||
        f.status === FileStatus.UPLOADING ||
        (isFileProcessing?.(f) ?? false);
      const isRemoved = (f: FileUploadType) =>
        f.status === FileStatus.DELETED || f.status === FileStatus.FAILED;
      return {
        inFlightFiles: files.filter(isInFlight),
        // Removed files last: they collapse to a stub and would otherwise gap
        // the grid between full-height cards.
        settledFiles: files
          .filter((f) => !isInFlight(f))
          .sort((a, b) => Number(isRemoved(a)) - Number(isRemoved(b))),
      };
    }, [files, isFileProcessing]);

    return (
      <Box sx={{ width: '100%' }} className={className}>
        <input
          type="file"
          ref={fileInputRef}
          multiple={multiple}
          accept={allowedFileTypes.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: 'none' }}
          disabled={disabled}
          aria-label={t('fileUpload.ariaLabel.fileInput')}
        />

        <Paper
          variant="outlined"
          sx={{
            backgroundColor:
              isDraggingOver && !disabled
                ? alpha(theme.palette.primary.light, 0.08)
                : 'background.paper',
            border:
              isDraggingOver && !disabled
                ? `2px solid ${theme.palette.primary.main}`
                : '2px dashed transparent',
            backgroundImage:
              disabled && isLarge
                ? `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(180deg, ${theme.palette.action.disabled}, ${theme.palette.action.disabled})`
                : `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            borderRadius: borderRadius.sm,
            p: 2,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            width: 'auto',
            transition: 'all 0.2s ease-in-out',
            boxShadow:
              isDraggingOver && !disabled
                ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`
                : 'none',
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onClick={handleBrowseClick}
          role="button"
          aria-label={t('fileUpload.ariaLabel.fileDropZone')}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              handleBrowseClick();
            }
          }}
        >
          <Box
            sx={{
              backgroundColor: isLarge
                ? disabled
                  ? 'action.disabledBackground'
                  : isDraggingOver && !disabled
                    ? alpha(theme.palette.primary.light, 0.15)
                    : 'grey.200'
                : 'transparent',
              p: isLarge ? 4 : 1,
              display: 'flex',
              flexDirection: isLarge ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: isLarge ? 'center' : 'flex-start',
              gap: 1,
              width: '100%',
              borderRadius: borderRadius.sm,
              transition: 'background-color 0.2s ease-in-out',
              position: 'relative',
              border: '1px solid',
              borderColor: 'grey.500',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isLarge ? 'column' : 'row',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                visibility: isDraggingOver && !disabled ? 'hidden' : 'visible',
                opacity: isDraggingOver && !disabled ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {isLarge && (
                <Paper
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: borderRadius.sm,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    boxShadow: 'none',
                    border: 'none',
                    width: 'auto',
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      width: 18.33,
                      height: 15.83,
                      fontSize: 14,
                      color: disabled ? 'text.disabled' : 'text.primary',
                    }}
                  />
                  <Box>
                    <Typography
                      variant="textSM_semiBold"
                      sx={{
                        color: disabled ? 'text.disabled' : 'text.primary',
                        fontWeight: 500,

                      }}
                    >
                      <Trans i18nKey="fileUpload.dragDrop.text" />
                    </Typography>
                  </Box>
                </Paper>
              )}

              {!isLarge && (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 24,
                      color: disabled ? 'text.disabled' : 'text.primary',
                    }}
                  />
                  <Typography
                    variant="textSM_semiBold"
                    sx={{
                      color: disabled ? 'text.disabled' : 'text.primary',
                      fontWeight: 500,

                    }}
                  >
                    <Trans i18nKey="fileUpload.dragDrop.text" />
                  </Typography>
                </>
              )}

              <NoFlipTypography
                mode={dir}
                variant="textSM_regular"
                sx={{
                  color:
                    disabled && isLarge
                      ? 'text.disabled'
                      : isLarge
                        ? 'text.primary'
                        : 'text.secondary',
                  opacity: disabled ? 0.9 : 1,
                  textAlign: dir === 'rtl' ? 'right' : 'left',
                  mt: isLarge ? 1 : 0,
                  fontWeight: 600,
                  fontSize: isLarge ? 14 : 12,

                  unicodeBidi: 'plaintext',
                }}
              >
                <Trans
                  i18nKey="fileUpload.supportedFormats"
                  values={{
                    fileTypes: allowedFileTypes.join(', ').toUpperCase(),
                    maxFileSize: maxFileSizeMb,
                  }}
                />
              </NoFlipTypography>
            </Box>

            {/* Drag active state - "Drop here" message */}
            {isDraggingOver && !disabled && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <Typography
                  variant="textSM_semiBold"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: isLarge ? 18 : 16,
                    textAlign: 'center',
                  }}
                >
                  <Trans i18nKey="fileUpload.dragDrop.dropHere" />
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {files.length > 0 && (
          <Box
            sx={{
              mt: 2,
              maxHeight: isLarge ? '600px' : '400px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'grey.100', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'grey.400',
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'grey.500' },
              },
            }}
          >
            {([
              { key: 'uploading', label: t('fileUpload.section.uploading'), items: inFlightFiles },
              { key: 'uploaded', label: t('fileUpload.section.uploaded'), items: settledFiles },
            ] as const)
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <Box key={group.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* The heading only appears once both groups are populated —
                      a single group needs no label to be understood. */}
                  {inFlightFiles.length > 0 && settledFiles.length > 0 && (
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {group.label} ({group.items.length})
                    </Typography>
                  )}
                        <Box
                          sx={{
                      // CSS Grid (not multi-column) — multi-column reflows the
                      // entire stack when one card resizes (e.g. classification
                      // accordion opens), shifting siblings left/right. Grid keeps
                      // each card pinned to its cell so accordion expansion only
                      // grows that row vertically. `auto-fill` + `minmax(…, 1fr)`
                      // also lets cards stretch to fill the row instead of getting
                      // stuck at the column-min width on wider screens.
                      display: 'grid',
                      gridTemplateColumns: isLarge
                        ? {
                          xs: '1fr',
                          sm: 'repeat(auto-fill, minmax(440px, 1fr))',
                        }
                        : '1fr',
                      gap: isLarge ? 3 : 2,
                      // Stretch, not start. Cards carry wildly different amounts —
                      // a removed file is two lines, a weak match with its picker is
                      // twenty — and with `start` every short card in a tall row left
                      // a hole beneath it. Stretching makes each row flush regardless
                      // of what any one card happens to contain.
                      alignItems: 'stretch',
              }}
            >
                                  {group.items.map((file) => (
                      <FileUploadItem
                        key={file.id}
                        file={file}
                        layout={sizeVariant}
                        onDelete={onDelete}
                        onCancel={onCancel}
                        onUndoDelete={onUndoDelete}
                        onRetryDelete={onRetryDelete}
                        onRetryUpload={onRetryUpload}
                        onRemoveFailedUpload={onRemoveFailedUpload}
                        isMoving={isFileMoving?.(file) ?? false}
                        renderExtraContent={renderExtraContent}
                        renderFileBadge={renderFileBadge}
                        deleteDisabled={isDeleteDisabled?.(file)}
                      />
              ))}
                  </Box>
                </Box>
              ))}
          </Box>
        )}
      </Box>
    );
  },
);
